import {useCurrentPageType} from '@aboutyou/router';
import {COLORS, StyledLogoText, Z_INDEXES} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {Suspense} from 'react';
import {AboutYouLogoProps} from '../../features/AboutYouLogo/AboutYouLogo';
import {HeaderAboutYouLogoWrapper} from './AboutYouLogo/HeaderAboutYouLogoWrapper';
import {GenderSwitch} from './GenderSwitch/GenderSwitch';
import {IconMetaNavigationItems} from './IconMetaNavigation/IconMetaNavigationItems';
import {RowSectionStyled} from './RowSectionStyled';
import {DESKTOP_STICKY_HEIGHT} from './constants';

interface HeaderTopRowContainerProps {
  notificationBellRef: React.RefObject<HTMLDivElement | null>;
  showSearchIcon: boolean;
  onSearchIconClick: () => void;
  searchIconRef: React.RefObject<HTMLDivElement | null>;
}

export const HeaderTopRowContainer: React.FC<HeaderTopRowContainerProps> = ({
  notificationBellRef,
  showSearchIcon,
  onSearchIconClick,
  searchIconRef,
}) => {
  const currentPageType = useCurrentPageType();
  const enableSticky = currentPageType !== 'couponLandingPage'; // coupon landing page already has a sticky coupon bar

  return (
    <Wrapper data-testid="headerTopRowContainer" enableSticky={enableSticky}>
      <TopRowContents>
        <Suspense>
          <StyledGenderSwitch />
        </Suspense>
        <StyledHeaderAboutYouLogoWrapper />
        <StyledIconMetaNavigationItems
          notificationBellRef={notificationBellRef}
          showSearchIcon={showSearchIcon}
          onSearchIconClick={onSearchIconClick}
          searchIconRef={searchIconRef}
        />
      </TopRowContents>
    </Wrapper>
  );
};

const Wrapper = styled.div<{enableSticky: boolean}>`
  width: 100%;
  height: ${DESKTOP_STICKY_HEIGHT}px;
  border-bottom: 1px solid ${COLORS.Grey4};
  position: ${props => (props.enableSticky ? 'sticky' : 'relative')};
  top: 0;
  z-index: ${Z_INDEXES.DESKTOP_HEADER};
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
`;

const TopRowContents = styled(RowSectionStyled)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  height: ${DESKTOP_STICKY_HEIGHT}px;
  margin: auto;
`;

const StyledGenderSwitch = styled(GenderSwitch)`
  flex: 1 2 33%;
`;

/**
 * This breakpoint is specific for the new header since the top row
 * contains more elements than the old header, we run into overflow-issues
 * with longer first names. The maximum amount of characters display is 18
 * (see: packages/core/ui-components/src/components/organisms/AboutYouMainLogo/AdditionalLogos/AboutNameLogo/AboutNameTextLogo.tsx).
 *
 * Above 805px width it is fine to display the full name, below that we need
 * to truncate it.
 */
const mediaMaxAYLogo = `only screen and (max-width: 805px)`;

// The tests are mocking many apis which does not work well in jest with circular dependencies. Deferring the logo fixes it.
const DeferredLogoWrapper: React.FC<AboutYouLogoProps> = props => (
  <HeaderAboutYouLogoWrapper {...props} />
);
const StyledHeaderAboutYouLogoWrapper = styled(DeferredLogoWrapper)`
  ${StyledLogoText} {
    @media ${mediaMaxAYLogo} {
      display: block;
      text-overflow: ellipsis;
      max-width: 220px;
      overflow: hidden;
    }
  }
`;

const StyledIconMetaNavigationItems = styled(IconMetaNavigationItems)`
  flex: 1 1 33%;
`;
